import React, { useState, useEffect } from 'react';

import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import { citiesService, subUnionsService } from '../../services/dataService';

// Import page components
import SubUnionCard from './components/SubUnionCard';
import FilterControls from './components/FilterControls';
import BulkActions from './components/BulkActions';
import ConfirmationDialog from './components/ConfirmationDialog';
import EmptyState from './components/EmptyState';
import LoadingState from './components/LoadingState';

const SubUnionsManagementContent = () => {
  const { success, error, warning } = useNotification();

  // UI State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  // Data State
  const [subUnions, setSubUnions] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    search: '',
    city: 'all',
    status: 'all',
    memberCount: {
      min: 0,
      max: 1000
    }
  });

  // Dialog State
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: 'warning',
    title: '',
    message: '',
    onConfirm: null
  });

  // Fetch cities and sub-unions from database
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetch cities and sub-unions in parallel
        const [citiesData, subUnionsData] = await Promise.all([
          citiesService.getAll(),
          subUnionsService.getAll()
        ]);

        // Map cities to expected format
        const mappedCities = citiesData?.map(city => ({
          id: city?.id,
          name: {
            ar: city?.nameAr || '',
            en: city?.nameEn || '',
            tr: city?.nameTr || ''
          },
          coordinates: {
            lat: city?.svgY || 0,
            lng: city?.svgX || 0
          }
        }));

        // Map sub-unions to expected format
        const mappedSubUnions = subUnionsData?.map(union => {
          // Find the city for this union
          const city = mappedCities?.find(c => c?.id === union?.cityId);
          
          return {
            id: union?.id,
            name: {
              ar: union?.nameAr || '',
              en: union?.nameEn || '',
              tr: union?.nameTr || ''
            },
            description: {
              ar: union?.cardDescriptionAr || union?.aboutDescriptionAr || '',
              en: union?.cardDescriptionEn || union?.aboutDescriptionEn || '',
              tr: union?.cardDescriptionTr || union?.aboutDescriptionTr || ''
            },
            logo: union?.logoUrl || '',
            logoAlt: `${union?.nameEn || union?.nameAr || 'Sub-Union'} logo`,
            city: city || null,
            memberCount: union?.memberCount || 0,
            isActive: union?.status === 'active',
            establishedDate: union?.establishedYear ? `${union.establishedYear}-01-01` : null,
            contactInfo: {
              email: union?.contactEmail || '',
              phone: union?.phone || union?.contactNumberAr || union?.contactNumberEn || '',
              address: ''
            },
            socialMedia: {},
            activities: [],
            lastActivity: union?.updatedAt || union?.createdAt || new Date().toISOString()
          };
        });

        setCities(mappedCities);
        setSubUnions(mappedSubUnions);
      } catch (err) {
        console.error('Failed to load data:', err);
        error('فشل في تحميل البيانات');
        setCities([]);
        setSubUnions([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [error]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter sub-unions
  const filteredSubUnions = subUnions?.filter((subUnion) => {
    const matchesSearch = !filters?.search ||
    subUnion?.name?.ar?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    subUnion?.name?.en?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
    subUnion?.name?.tr?.toLowerCase()?.includes(filters?.search?.toLowerCase());

    const matchesCity = filters?.city === 'all' || subUnion?.city?.id === filters?.city;

    const matchesStatus = filters?.status === 'all' ||
    filters?.status === 'active' && subUnion?.isActive ||
    filters?.status === 'inactive' && !subUnion?.isActive;

    const matchesMemberCount = subUnion?.memberCount >= filters?.memberCount?.min &&
    subUnion?.memberCount <= filters?.memberCount?.max;

    return matchesSearch && matchesCity && matchesStatus && matchesMemberCount;
  });

  // Handlers
  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      city: 'all',
      status: 'all',
      memberCount: { min: 0, max: 1000 }
    });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
    prev?.includes(id) ?
    prev?.filter((item) => item !== id) :
    [...prev, id]
    );
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleBulkAction = async (action, items) => {
    if (action === 'selectAll') {
      setSelectedItems(filteredSubUnions?.map((su) => su?.id));
      return;
    }

    try {
      switch (action) {
        case 'activate':
          // Fetch full data from database and update status to 'active' for all selected items
          await Promise.all(
            items.map(async id => {
              try {
                const dbUnion = await subUnionsService.getById(id);
                if (!dbUnion) return Promise.resolve();

                // Update only the status field, keeping all other fields
                const updatePayload = {
                  nameAr: dbUnion.nameAr,
                  nameEn: dbUnion.nameEn,
                  nameTr: dbUnion.nameTr,
                  phone: dbUnion.phone,
                  contactEmail: dbUnion.contactEmail,
                  cityId: dbUnion.cityId,
                  status: 'active',
                  memberCount: dbUnion.memberCount,
                  establishedYear: dbUnion.establishedYear,
                  logoUrl: dbUnion.logoUrl,
                  contactNumberAr: dbUnion.contactNumberAr,
                  contactNumberEn: dbUnion.contactNumberEn,
                  contactNumberTr: dbUnion.contactNumberTr,
                  cardDescriptionAr: dbUnion.cardDescriptionAr,
                  cardDescriptionEn: dbUnion.cardDescriptionEn,
                  cardDescriptionTr: dbUnion.cardDescriptionTr,
                  aboutDescriptionAr: dbUnion.aboutDescriptionAr,
                  aboutDescriptionEn: dbUnion.aboutDescriptionEn,
                  aboutDescriptionTr: dbUnion.aboutDescriptionTr,
                  specialAchievementsAr: dbUnion.specialAchievementsAr,
                  specialAchievementsEn: dbUnion.specialAchievementsEn,
                  specialAchievementsTr: dbUnion.specialAchievementsTr,
                  activitiesAndAchievementsAr: dbUnion.activitiesAndAchievementsAr,
                  activitiesAndAchievementsEn: dbUnion.activitiesAndAchievementsEn,
                  activitiesAndAchievementsTr: dbUnion.activitiesAndAchievementsTr,
                  currentAdministrativeTeamAr: dbUnion.currentAdministrativeTeamAr,
                  currentAdministrativeTeamEn: dbUnion.currentAdministrativeTeamEn,
                  currentAdministrativeTeamTr: dbUnion.currentAdministrativeTeamTr,
                  notesAr: dbUnion.notesAr,
                  notesEn: dbUnion.notesEn,
                  notesTr: dbUnion.notesTr
                };

                return subUnionsService.update(id, updatePayload);
              } catch (err) {
                console.error(`Error fetching sub-union ${id}:`, err);
                return Promise.resolve();
              }
            })
          );

          // Refresh data from database
          const [citiesData, subUnionsData] = await Promise.all([
            citiesService.getAll(),
            subUnionsService.getAll()
          ]);

          const mappedCities = citiesData?.map(city => ({
            id: city?.id,
            name: {
              ar: city?.nameAr || '',
              en: city?.nameEn || '',
              tr: city?.nameTr || ''
            },
            coordinates: {
              lat: city?.svgY || 0,
              lng: city?.svgX || 0
            }
          }));

          const mappedSubUnions = subUnionsData?.map(union => {
            const city = mappedCities?.find(c => c?.id === union?.cityId);
            
            return {
              id: union?.id,
              name: {
                ar: union?.nameAr || '',
                en: union?.nameEn || '',
                tr: union?.nameTr || ''
              },
              description: {
                ar: union?.cardDescriptionAr || union?.aboutDescriptionAr || '',
                en: union?.cardDescriptionEn || union?.aboutDescriptionEn || '',
                tr: union?.cardDescriptionTr || union?.aboutDescriptionTr || ''
              },
              logo: union?.logoUrl || '',
              logoAlt: `${union?.nameEn || union?.nameAr || 'Sub-Union'} logo`,
              city: city || null,
              memberCount: union?.memberCount || 0,
              isActive: union?.status === 'active',
              establishedDate: union?.establishedYear ? `${union.establishedYear}-01-01` : null,
              contactInfo: {
                email: union?.contactEmail || '',
                phone: union?.phone || union?.contactNumberAr || union?.contactNumberEn || '',
                address: ''
              },
              socialMedia: {},
              activities: [],
              lastActivity: union?.updatedAt || union?.createdAt || new Date().toISOString()
            };
          });

          setCities(mappedCities);
          setSubUnions(mappedSubUnions);
          success(`تم تفعيل ${items?.length} اتحاد فرعي بنجاح`);
          break;

        case 'deactivate':
          // Fetch full data from database and update status to 'inactive' for all selected items
          await Promise.all(
            items.map(async id => {
              try {
                const dbUnion = await subUnionsService.getById(id);
                if (!dbUnion) return Promise.resolve();

                // Update only the status field, keeping all other fields
                const updatePayload = {
                  nameAr: dbUnion.nameAr,
                  nameEn: dbUnion.nameEn,
                  nameTr: dbUnion.nameTr,
                  phone: dbUnion.phone,
                  contactEmail: dbUnion.contactEmail,
                  cityId: dbUnion.cityId,
                  status: 'inactive',
                  memberCount: dbUnion.memberCount,
                  establishedYear: dbUnion.establishedYear,
                  logoUrl: dbUnion.logoUrl,
                  contactNumberAr: dbUnion.contactNumberAr,
                  contactNumberEn: dbUnion.contactNumberEn,
                  contactNumberTr: dbUnion.contactNumberTr,
                  cardDescriptionAr: dbUnion.cardDescriptionAr,
                  cardDescriptionEn: dbUnion.cardDescriptionEn,
                  cardDescriptionTr: dbUnion.cardDescriptionTr,
                  aboutDescriptionAr: dbUnion.aboutDescriptionAr,
                  aboutDescriptionEn: dbUnion.aboutDescriptionEn,
                  aboutDescriptionTr: dbUnion.aboutDescriptionTr,
                  specialAchievementsAr: dbUnion.specialAchievementsAr,
                  specialAchievementsEn: dbUnion.specialAchievementsEn,
                  specialAchievementsTr: dbUnion.specialAchievementsTr,
                  activitiesAndAchievementsAr: dbUnion.activitiesAndAchievementsAr,
                  activitiesAndAchievementsEn: dbUnion.activitiesAndAchievementsEn,
                  activitiesAndAchievementsTr: dbUnion.activitiesAndAchievementsTr,
                  currentAdministrativeTeamAr: dbUnion.currentAdministrativeTeamAr,
                  currentAdministrativeTeamEn: dbUnion.currentAdministrativeTeamEn,
                  currentAdministrativeTeamTr: dbUnion.currentAdministrativeTeamTr,
                  notesAr: dbUnion.notesAr,
                  notesEn: dbUnion.notesEn,
                  notesTr: dbUnion.notesTr
                };

                return subUnionsService.update(id, updatePayload);
              } catch (err) {
                console.error(`Error fetching sub-union ${id}:`, err);
                return Promise.resolve();
              }
            })
          );

          // Refresh data from database
          const [citiesData2, subUnionsData2] = await Promise.all([
            citiesService.getAll(),
            subUnionsService.getAll()
          ]);

          const mappedCities2 = citiesData2?.map(city => ({
            id: city?.id,
            name: {
              ar: city?.nameAr || '',
              en: city?.nameEn || '',
              tr: city?.nameTr || ''
            },
            coordinates: {
              lat: city?.svgY || 0,
              lng: city?.svgX || 0
            }
          }));

          const mappedSubUnions2 = subUnionsData2?.map(union => {
            const city = mappedCities2?.find(c => c?.id === union?.cityId);
            
            return {
              id: union?.id,
              name: {
                ar: union?.nameAr || '',
                en: union?.nameEn || '',
                tr: union?.nameTr || ''
              },
              description: {
                ar: union?.cardDescriptionAr || union?.aboutDescriptionAr || '',
                en: union?.cardDescriptionEn || union?.aboutDescriptionEn || '',
                tr: union?.cardDescriptionTr || union?.aboutDescriptionTr || ''
              },
              logo: union?.logoUrl || '',
              logoAlt: `${union?.nameEn || union?.nameAr || 'Sub-Union'} logo`,
              city: city || null,
              memberCount: union?.memberCount || 0,
              isActive: union?.status === 'active',
              establishedDate: union?.establishedYear ? `${union.establishedYear}-01-01` : null,
              contactInfo: {
                email: union?.contactEmail || '',
                phone: union?.phone || union?.contactNumberAr || union?.contactNumberEn || '',
                address: ''
              },
              socialMedia: {},
              activities: [],
              lastActivity: union?.updatedAt || union?.createdAt || new Date().toISOString()
            };
          });

          setCities(mappedCities2);
          setSubUnions(mappedSubUnions2);
          success(`تم إلغاء تفعيل ${items?.length} اتحاد فرعي بنجاح`);
          break;

        case 'export':
          // Export functionality (client-side)
          const exportData = subUnions.filter(su => items.includes(su.id));
          console.log('Exporting sub-unions:', exportData);
          success(`تم تصدير بيانات ${items?.length} اتحاد فرعي`);
          break;

        case 'delete':
          setConfirmDialog({
            isOpen: true,
            type: 'danger',
            title: 'تأكيد الحذف',
            message: `هل أنت متأكد من حذف ${items?.length} اتحاد فرعي؟ هذا الإجراء لا يمكن التراجع عنه.`,
            onConfirm: async () => {
              try {
                await Promise.all(items.map(id => subUnionsService.delete(id)));
                
                // Refresh data from database
                const [citiesData3, subUnionsData3] = await Promise.all([
                  citiesService.getAll(),
                  subUnionsService.getAll()
                ]);

                const mappedCities3 = citiesData3?.map(city => ({
                  id: city?.id,
                  name: {
                    ar: city?.nameAr || '',
                    en: city?.nameEn || '',
                    tr: city?.nameTr || ''
                  },
                  coordinates: {
                    lat: city?.svgY || 0,
                    lng: city?.svgX || 0
                  }
                }));

                const mappedSubUnions3 = subUnionsData3?.map(union => {
                  const city = mappedCities3?.find(c => c?.id === union?.cityId);
                  
                  return {
                    id: union?.id,
                    name: {
                      ar: union?.nameAr || '',
                      en: union?.nameEn || '',
                      tr: union?.nameTr || ''
                    },
                    description: {
                      ar: union?.cardDescriptionAr || union?.aboutDescriptionAr || '',
                      en: union?.cardDescriptionEn || union?.aboutDescriptionEn || '',
                      tr: union?.cardDescriptionTr || union?.aboutDescriptionTr || ''
                    },
                    logo: union?.logoUrl || '',
                    logoAlt: `${union?.nameEn || union?.nameAr || 'Sub-Union'} logo`,
                    city: city || null,
                    memberCount: union?.memberCount || 0,
                    isActive: union?.status === 'active',
                    establishedDate: union?.establishedYear ? `${union.establishedYear}-01-01` : null,
                    contactInfo: {
                      email: union?.contactEmail || '',
                      phone: union?.phone || union?.contactNumberAr || union?.contactNumberEn || '',
                      address: ''
                    },
                    socialMedia: {},
                    activities: [],
                    lastActivity: union?.updatedAt || union?.createdAt || new Date().toISOString()
                  };
                });

                setCities(mappedCities3);
                setSubUnions(mappedSubUnions3);
                success(`تم حذف ${items?.length} اتحاد فرعي بنجاح`);
                setConfirmDialog({ ...confirmDialog, isOpen: false });
              } catch (err) {
                console.error('Error deleting sub-unions:', err);
                error('فشل في حذف الاتحادات الفرعية');
                setConfirmDialog({ ...confirmDialog, isOpen: false });
              }
            }
          });
          return;
      }

      setSelectedItems([]);
    } catch (error) {
      console.error('Bulk action failed:', error);
      error('فشل في تنفيذ العملية');
    }
  };

  const handleEdit = (id) => {
    window.location.href = `/sub-union-form?id=${id}`;
  };

  const handleView = (id) => {
    const subUnion = subUnions?.find((su) => su?.id === id);
    if (subUnion) {
      success(`عرض تفاصيل: ${subUnion?.name?.ar}`);
    }
  };

  const handleToggleStatus = async (id, newStatus) => {
    const subUnion = subUnions?.find((su) => su?.id === id);
    if (!subUnion) return;

    const action = newStatus ? 'تفعيل' : 'إلغاء تفعيل';

    setConfirmDialog({
      isOpen: true,
      type: newStatus ? 'success' : 'warning',
      title: `${action} الاتحاد الفرعي`,
      message: `هل أنت متأكد من ${action} "${subUnion?.name?.ar}"؟`,
      onConfirm: async () => {
        try {
          // Fetch full data from database first
          const dbUnion = await subUnionsService.getById(id);
          if (!dbUnion) {
            error('فشل في جلب بيانات الاتحاد الفرعي');
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            return;
          }

          // Update only the status field, keeping all other fields
          const updatePayload = {
            nameAr: dbUnion.nameAr,
            nameEn: dbUnion.nameEn,
            nameTr: dbUnion.nameTr,
            phone: dbUnion.phone,
            contactEmail: dbUnion.contactEmail,
            cityId: dbUnion.cityId,
            status: newStatus ? 'active' : 'inactive',
            memberCount: dbUnion.memberCount,
            establishedYear: dbUnion.establishedYear,
            logoUrl: dbUnion.logoUrl,
            contactNumberAr: dbUnion.contactNumberAr,
            contactNumberEn: dbUnion.contactNumberEn,
            contactNumberTr: dbUnion.contactNumberTr,
            cardDescriptionAr: dbUnion.cardDescriptionAr,
            cardDescriptionEn: dbUnion.cardDescriptionEn,
            cardDescriptionTr: dbUnion.cardDescriptionTr,
            aboutDescriptionAr: dbUnion.aboutDescriptionAr,
            aboutDescriptionEn: dbUnion.aboutDescriptionEn,
            aboutDescriptionTr: dbUnion.aboutDescriptionTr,
            specialAchievementsAr: dbUnion.specialAchievementsAr,
            specialAchievementsEn: dbUnion.specialAchievementsEn,
            specialAchievementsTr: dbUnion.specialAchievementsTr,
            activitiesAndAchievementsAr: dbUnion.activitiesAndAchievementsAr,
            activitiesAndAchievementsEn: dbUnion.activitiesAndAchievementsEn,
            activitiesAndAchievementsTr: dbUnion.activitiesAndAchievementsTr,
            currentAdministrativeTeamAr: dbUnion.currentAdministrativeTeamAr,
            currentAdministrativeTeamEn: dbUnion.currentAdministrativeTeamEn,
            currentAdministrativeTeamTr: dbUnion.currentAdministrativeTeamTr,
            notesAr: dbUnion.notesAr,
            notesEn: dbUnion.notesEn,
            notesTr: dbUnion.notesTr
          };

          await subUnionsService.update(id, updatePayload);

          // Refresh data from database
          const [citiesData, subUnionsData] = await Promise.all([
            citiesService.getAll(),
            subUnionsService.getAll()
          ]);

          const mappedCities = citiesData?.map(city => ({
            id: city?.id,
            name: {
              ar: city?.nameAr || '',
              en: city?.nameEn || '',
              tr: city?.nameTr || ''
            },
            coordinates: {
              lat: city?.svgY || 0,
              lng: city?.svgX || 0
            }
          }));

          const mappedSubUnions = subUnionsData?.map(union => {
            const city = mappedCities?.find(c => c?.id === union?.cityId);
            
            return {
              id: union?.id,
              name: {
                ar: union?.nameAr || '',
                en: union?.nameEn || '',
                tr: union?.nameTr || ''
              },
              description: {
                ar: union?.cardDescriptionAr || union?.aboutDescriptionAr || '',
                en: union?.cardDescriptionEn || union?.aboutDescriptionEn || '',
                tr: union?.cardDescriptionTr || union?.aboutDescriptionTr || ''
              },
              logo: union?.logoUrl || '',
              logoAlt: `${union?.nameEn || union?.nameAr || 'Sub-Union'} logo`,
              city: city || null,
              memberCount: union?.memberCount || 0,
              isActive: union?.status === 'active',
              establishedDate: union?.establishedYear ? `${union.establishedYear}-01-01` : null,
              contactInfo: {
                email: union?.contactEmail || '',
                phone: union?.phone || union?.contactNumberAr || union?.contactNumberEn || '',
                address: ''
              },
              socialMedia: {},
              activities: [],
              lastActivity: union?.updatedAt || union?.createdAt || new Date().toISOString()
            };
          });

          setCities(mappedCities);
          setSubUnions(mappedSubUnions);
          success(`تم ${action} الاتحاد الفرعي بنجاح`);
        } catch (error) {
          console.error(`Error ${action} sub-union:`, error);
          error(`فشل في ${action} الاتحاد الفرعي`);
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  const handleAddSubUnion = () => {
    window.location.href = '/sub-union-form';
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingState viewMode={viewMode} />;
    }

    if (filteredSubUnions?.length === 0) {
      if (subUnions?.length === 0) {
        return <EmptyState type="no-data" onAction={handleAddSubUnion} onClearFilters={handleClearFilters} />;
      } else {
        return (
          <EmptyState
            type="no-results"
            hasFilters={true}
            onClearFilters={handleClearFilters}
            onAction={handleAddSubUnion} />);


      }
    }

    if (viewMode === 'table') {
      return (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={selectedItems?.length === filteredSubUnions?.length}
                    onChange={() => {
                      if (selectedItems?.length === filteredSubUnions?.length) {
                        setSelectedItems([]);
                      } else {
                        setSelectedItems(filteredSubUnions?.map((su) => su?.id));
                      }
                    }}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary" />

                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">الاسم</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">المدينة</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">عدد الأعضاء</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">الحالة</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-foreground">الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubUnions?.map((subUnion) =>
              <SubUnionCard
                key={subUnion?.id}
                subUnion={subUnion}
                onEdit={handleEdit}
                onView={handleView}
                onToggleStatus={handleToggleStatus}
                onSelect={handleSelectItem}
                isSelected={selectedItems?.includes(subUnion?.id)}
                viewMode="table" />

              )}
            </tbody>
          </table>
        </div>);

    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredSubUnions?.map((subUnion) =>
        <SubUnionCard
          key={subUnion?.id}
          subUnion={subUnion}
          onEdit={handleEdit}
          onView={handleView}
          onToggleStatus={handleToggleStatus}
          onSelect={handleSelectItem}
          isSelected={selectedItems?.includes(subUnion?.id)}
          viewMode="grid" />

        )}
      </div>);

  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={isMenuOpen} />
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleSidebarToggle} />

      <main className={`transition-all duration-300 ${
      isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} mt-16`
      }>
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <Breadcrumbs />
              <div className="mt-2">
                <h1 className="text-2xl font-bold text-foreground font-arabic">
                  إدارة الاتحادات الفرعية
                </h1>
                <p className="text-muted-foreground mt-1">
                  إدارة وتنظيم الاتحادات الفرعية للطلاب السوريين في مختلف المدن
                </p>
              </div>
            </div>
            
            <Button
              variant="default"
              onClick={handleAddSubUnion}
              iconName="Plus"
              iconPosition="left"
              iconSize={20}
              className="px-6">

              إضافة اتحاد فرعي
            </Button>
          </div>

          {/* Filter Controls */}
          <FilterControls
            filters={filters}
            onFiltersChange={handleFiltersChange}
            cities={cities}
            totalCount={subUnions?.length}
            filteredCount={filteredSubUnions?.length}
            onClearFilters={handleClearFilters}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange} />


          {/* Bulk Actions */}
          <BulkActions
            selectedItems={selectedItems}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
            totalItems={filteredSubUnions?.length} />


          {/* Content */}
          {renderContent()}

          {/* Pagination would go here for large datasets */}
          {filteredSubUnions?.length > 20 &&
          <div className="flex items-center justify-center pt-6">
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm" disabled>
                  السابق
                </Button>
                <div className="flex items-center space-x-1">
                  <Button variant="default" size="sm">1</Button>
                  <Button variant="ghost" size="sm">2</Button>
                  <Button variant="ghost" size="sm">3</Button>
                </div>
                <Button variant="outline" size="sm">
                  التالي
                </Button>
              </div>
            </div>
          }
        </div>
      </main>
      {/* Floating Action Button */}
      <QuickActionButton />
      {/* Search Command Palette */}
      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)} />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmDialog?.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog?.onConfirm}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type} />

    </div>);

};

const SubUnionsManagement = () => {
  return (
    <NotificationProvider>
      <SubUnionsManagementContent />
    </NotificationProvider>);

};

export default SubUnionsManagement;